import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Camera, CircleUser } from "lucide-react";
import Modal from "@/components/Modal";
import { AUTH_CARD_CLASS } from "@/components/auth/authStyles";
import { readImageFileAsDataUrl } from "@/utils/imageFile";
import {
  buildSignupQuery,
  getSignupProfile,
  readSignupParams,
  setSignupProfile,
} from "./signupState";

/**
 * Signup step 3 — profile icon. Ports the RN `SetupProfileIconScreen`.
 * The picked image is downscaled, then kept as a data URL in signup state
 * (too large for a URL param) and uploaded later during registration.
 * The store is the source of truth, so the preview rehydrates on re-entry.
 */
const ProfileIconPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = readSignupParams(search);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState<string>(getSignupProfile);
  const [imageError, setImageError] = useState("");
  const [modalAlertVisible, setModalAlertVisible] = useState(false);
  const [modalAlertText, setModalAlertText] = useState("");

  const pickImage = async (file?: File) => {
    if (!file) return;
    const dataUrl = await readImageFileAsDataUrl(file, { maxEdge: 512 });
    if (!dataUrl) return;
    setProfile(dataUrl);
    setImageError(
      setSignupProfile(dataUrl) ? "" : t("Could not cache this photo. It will still be uploaded when you register."),
    );
  };

  const goNext = () => {
    navigate(
      `/signup/student-card${buildSignupQuery({
        ...params,
        profile: profile ? "set" : "",
      })}`,
    );
  };

  const onPressNext = () => {
    if (!profile) {
      setModalAlertText(t("Please select your profile icon"));
      return setModalAlertVisible(true);
    }
    goNext();
  };

  const onPressSkip = () => {
    setProfile("");
    setSignupProfile("");
    goNext();
  };

  return (
    <div className={AUTH_CARD_CLASS}>
      <h1 className="text-2xl font-extrabold text-[#333]">
        {t("Custom your Profile icon")}
      </h1>

      <div className="mt-8 flex flex-col items-center">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-40 w-40 items-center justify-center overflow-hidden rounded-full border-4 border-dashed border-[#ffa217] bg-[#fff8f0] transition hover:bg-[#fff3e0]"
        >
          {profile ? (
            <img src={profile} alt="Profile" className="h-full w-full object-cover" />
          ) : (
            <CircleUser size={80} className="text-[#ffa217]" />
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => pickImage(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#ffa217] transition hover:text-[#e08f0e]"
        >
          <Camera size={16} />
          {profile ? t("Change photo") : t("Pick an image")}
        </button>
        {imageError && (
          <p className="mt-2 max-w-xs text-center text-sm font-medium text-red-500">
            {imageError}
          </p>
        )}
      </div>

      <div className="mt-8 space-y-3">
        <button
          type="button"
          onClick={onPressNext}
          className="w-full rounded-lg bg-[#ffa217] py-3 font-bold text-white transition hover:bg-[#e08f0e]"
        >
          {t("NEXT")}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/signup/verify${buildSignupQuery({ ...params })}`)}
          className="w-full rounded-lg bg-red-500 py-3 font-bold text-white transition hover:bg-red-600"
        >
          {t("BACK")}
        </button>
        <button
          type="button"
          onClick={onPressSkip}
          className="w-full rounded-lg bg-gray-400 py-3 font-bold text-white transition hover:bg-gray-500"
        >
          {t("SKIP")}
        </button>
      </div>

      <Modal visible={modalAlertVisible} onClose={() => setModalAlertVisible(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-[#333]">{modalAlertText}</div>
          <button
            type="button"
            onClick={() => setModalAlertVisible(false)}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ProfileIconPage;